/**
 * Workflow Field Permissions Utilities
 * 
 * This module contains utility functions to determine if workflow fields
 * should be disabled based on status and permissions.
 * 
 * Extracted from useInvoicePermissions to reduce complexity and improve maintainability.
 */

import {
  STORE_FIELD_HIDDEN_STATUSES,
  PO_SO_HIDDEN_STATUSES,
  IR_HIDDEN_STATUSES,
  INV_HIDDEN_STATUSES,
  ASSIGNMENT_REQUIRED_STATUSES,
  ALWAYS_VISIBLE_STATUSES,
  ADMIN_REQUIRED_STATUSES,
  INVOICE_STATUSES,
  type InvoiceStatusKey
} from '@constants/invoiceStatus';

// Type definitions for cleaner function signatures
type WorkflowFieldType = 'store' | 'po' | 'so' | 'ir' | 'inv';

// Type guard to check if a status is a valid invoice status
const isValidInvoiceStatus = (status: string): status is InvoiceStatusKey => {
  return Object.values(INVOICE_STATUSES).includes(status as InvoiceStatusKey);
};

// Type-safe status validation for status arrays
const isStatusInArray = <T extends readonly string[]>(
  status: string, 
  validStatuses: T
): status is T[number] => {
  return isValidInvoiceStatus(status) && validStatuses.includes(status);
};

type FieldPermissionContext = {
  currentStatus: string;
  hasRole: (role: string) => boolean;
  canEditWorkflowFields: () => boolean;
};

// Use centralized status constants to avoid magic strings
const { NEW_PRODUCTS, IR, INVOICING, ADDING, UNPROCESSED } = INVOICE_STATUSES;

/**
 * Determines if a field is hidden for the current status
 */
const isFieldHidden = (fieldType: WorkflowFieldType, currentStatus: string): boolean => {
  // Validate status before checking arrays
  if (!isValidInvoiceStatus(currentStatus)) {
    return true; // Hide field for invalid statuses
  }

  switch (fieldType) {
    case 'store':
      return isStatusInArray(currentStatus, STORE_FIELD_HIDDEN_STATUSES);
    case 'po':
    case 'so':
      return isStatusInArray(currentStatus, PO_SO_HIDDEN_STATUSES);
    case 'ir':
      return isStatusInArray(currentStatus, IR_HIDDEN_STATUSES);
    case 'inv':
      return isStatusInArray(currentStatus, INV_HIDDEN_STATUSES);
    default:
      return true;
  }
};

/**
 * Determines if a field requires admin permissions for the current status
 */
const requiresAdminPermission = (fieldType: WorkflowFieldType, currentStatus: string): boolean => {
  // Store field admin requirements
  if (fieldType === 'store') {
    return [NEW_PRODUCTS, IR, INVOICING].includes(currentStatus);
  }
  
  // PO/SO field admin requirements
  if (fieldType === 'po' || fieldType === 'so') {
    return [NEW_PRODUCTS, IR, INVOICING].includes(currentStatus);
  }
  
  // IR field - admin required for all statuses except 'ir' itself
  if (fieldType === 'ir') {
    return currentStatus !== IR;
  }
  
  // INV field - admin required for all statuses except 'invoicing' itself
  if (fieldType === 'inv') {
    return currentStatus !== INVOICING;
  }
  
  return false;
};

/**
 * Determines if a workflow field should be disabled
 */
export const isWorkflowFieldDisabled = (
  fieldType: WorkflowFieldType,
  context: FieldPermissionContext
): boolean => {
  const { currentStatus, hasRole, canEditWorkflowFields } = context;
  
  // Quick exit for unprocessed status - all workflow fields are hidden
  // BUT: Allow admin/finance users to transition FROM unprocessed to other statuses
  if (currentStatus === UNPROCESSED) {
    // Special case: Allow admin/finance to transition from unprocessed
    // This is handled by checking if the user has admin/finance role
    if (hasRole('admin') || hasRole('finance')) {
      return false; // Allow transition for admin/finance
    }
    return true;
  }
  
  // Quick exit if field is hidden for this status, unless admin/finance can override
  if (isFieldHidden(fieldType, currentStatus)) {
    // Allow admin/finance users to override hidden fields in ADMIN_REQUIRED_STATUSES
    if (isStatusInArray(currentStatus, ADMIN_REQUIRED_STATUSES) && (hasRole('admin') || hasRole('finance'))) {
      return false; // Admin/Finance can enable hidden fields in admin-required statuses
    }
    return true;
  }
  
  // For assignment-required statuses, check assignment first
  if (isStatusInArray(currentStatus, ASSIGNMENT_REQUIRED_STATUSES)) {
    const canEditWorkflow = canEditWorkflowFields();
    
    if (!canEditWorkflow) {
      return true; // Disabled if not admin or not assigned
    }
    
    // For assignment-required statuses where the field is the primary field,
    // it's enabled if assignment check passed
    const isPrimaryField = (
      (currentStatus === ADDING && ['store', 'po', 'so'].includes(fieldType)) ||
      (currentStatus === IR && fieldType === 'ir') ||
      (currentStatus === INVOICING && fieldType === 'inv')
    );
    
    if (isPrimaryField) {
      return false;
    }
    
    // For non-primary fields in assignment-required statuses,
    // still need to check admin/finance requirements
    const requiresAdmin = requiresAdminPermission(fieldType, currentStatus);
    if (requiresAdmin) {
      const hasAdminOrFinance = hasRole('admin') || hasRole('finance');
      return !hasAdminOrFinance;
    }
    
    // If no special admin requirements, allow editing since assignment check passed
    return false;
  }
  
  // Special case: IR and INV fields are always enabled when not hidden,
  // and always enabled in ALWAYS_VISIBLE_STATUSES regardless of admin requirements
  if (fieldType === 'ir' || fieldType === 'inv') {
    // Always enable in ALWAYS_VISIBLE_STATUSES
    if (isStatusInArray(currentStatus, ALWAYS_VISIBLE_STATUSES)) {
      return false;
    }
    // Check if they require admin/finance permission for this status
    if (requiresAdminPermission(fieldType, currentStatus)) {
      return !(hasRole('admin') || hasRole('finance'));
    }
    return false;
  }
  
  // Check admin/finance permission requirements for other fields in non-assignment-required statuses
  if (requiresAdminPermission(fieldType, currentStatus)) {
    return !(hasRole('admin') || hasRole('finance'));
  }
  
  // Default: only admin/finance can edit for non-specific cases
  return !(hasRole('admin') || hasRole('finance'));
};

/**
 * Factory function to create a field permission checker with context
 */
export const createWorkflowFieldChecker = (context: FieldPermissionContext) => ({
  isDisabled: (fieldType: WorkflowFieldType) => isWorkflowFieldDisabled(fieldType, context),
});
